import React, { useEffect, useState } from "react";
import {
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableHead,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
} from "@mui/material";
import type { InvestorStat } from "investor/types/types-invoices";
import { useGetInvestorsSummaryQuery } from "api/api-invoices";
import { useGetCurrentUserQuery } from "api/api-users-v2";

interface InvestorStatsProps {
  data?: any;
  userId?: string;
}

const InvestorStats: React.FC<InvestorStatsProps> = ({ data, userId }) => {
  const { data: currentUser } = useGetCurrentUserQuery();
  
  // Extract id from current user or use the provided userId
  const idToUse = userId || currentUser?.id;

  const [amount, setAmount] = useState<InvestorStat>({
    numberOfInvoicesFinanced: 0,
    averageInvoiceSize: 0,
    averageInvoiceTenor: 0,
    invoicesCoveredByCreditInsurance: "",
    averageRatingOfCreditInsurer: "",
    maximumExposurePerDebtor: "",
    averageExposurePerDebtor: "",
    invoicesOverdueOver30Days: "0",
    numberOfDefaults: "0",
  });

  // Use the investor summary API to get the data
  const { data: investorSummaryData } = useGetInvestorsSummaryQuery(
    { year: 'all', userId: idToUse as string },
    { 
      skip: !!data || !idToUse
    }
  );

  // Use either the data from props or summary API
  const invoiceData = data || investorSummaryData;

  useEffect(() => {
    if (invoiceData?.invoices) {
      const invoices = invoiceData.invoices;
      const numberOfInvoicesFinanced = invoices.length;
      
      let totalPrincipleAmount = 0;
      let numberOfDefaults = 0;
      let invoicesOverdueOver30Days = 0;
      let totalTenorDays = 0;
      
      invoices.forEach((inv: any) => {
        // Sum up principle amount
        totalPrincipleAmount += (inv.principle_amount || inv.principal_amount || 0);
        
        // Sum up tenor days if available
        if (inv.days_financed) {
          totalTenorDays += inv.days_financed;
        }
        
        // Check for overdue invoices
        if (inv.due_date) {
          const dueDate = new Date(inv.due_date);
          const now = new Date();
          
          // Check if invoice is defaulted
          if (now > dueDate && inv.status !== "PAID") {
            numberOfDefaults += 1;
          }
          
          // Calculate days overdue
          const diffInDays = Math.floor(
            (now.getTime() - dueDate.getTime()) / (1000 * 60 * 60 * 24)
          );
          
          // Check if overdue more than 30 days
          if (diffInDays > 30) {
            invoicesOverdueOver30Days += 1;
          }
        }
      });
      
      // Calculate average invoice size
      const averageInvoiceSize = numberOfInvoicesFinanced > 0 
        ? totalPrincipleAmount / numberOfInvoicesFinanced 
        : 0;
        
      // Calculate average tenor
      const averageInvoiceTenor = numberOfInvoicesFinanced > 0
        ? totalTenorDays / numberOfInvoicesFinanced
        : 0;

      setAmount({
        numberOfInvoicesFinanced,
        averageInvoiceSize,
        averageInvoiceTenor,
        invoicesCoveredByCreditInsurance: "",
        averageRatingOfCreditInsurer: "",
        maximumExposurePerDebtor: "",
        averageExposurePerDebtor: "",
        invoicesOverdueOver30Days: invoicesOverdueOver30Days.toString(),
        numberOfDefaults: numberOfDefaults.toString(),
      });
    }
  }, [invoiceData]);

  const formatCurrency = (amount: number) => {
    if (isNaN(amount) || amount === 0) return "$0";
    return `$${Math.round(amount).toLocaleString()}`;
  };

  return (
    <Card
      sx={{ 
        backgroundColor: "#f0f0f0", 
        padding: 2, 
        height: "100%",
        boxShadow: "0px 3px 6px rgba(0, 0, 0, 0.1)",
        borderRadius: 2
      }}
    >
      <CardContent sx={{ padding: 0 }}>
        <Typography
          variant="h4"
          gutterBottom
          sx={{ fontWeight: 'bold', mb: 2 }}
        >
          Statistics
        </Typography>
        <TableContainer
          component={Paper}
          sx={{ 
            backgroundColor: "#fff", 
            boxShadow: "none", 
            borderRadius: 1,
            overflow: "hidden"
          }}
        >
          <Table size="small">
            <TableHead sx={{ backgroundColor: "#f5f5f5" }}>
              {/* <TableRow>
                <TableCell sx={{ fontWeight: "bold", py: 1.5 }}>Metric</TableCell>
                <TableCell align="right" sx={{ fontWeight: "bold", py: 1.5 }}>Value</TableCell>
              </TableRow> */}
            </TableHead>
            <TableBody>
              <TableRow sx={{ '&:nth-of-type(even)': { backgroundColor: "#f9f9f9" } }}>
                <TableCell>Number Of Invoices Financed</TableCell>
                <TableCell align="right">{amount.numberOfInvoicesFinanced}</TableCell>
              </TableRow>
              <TableRow sx={{ '&:nth-of-type(even)': { backgroundColor: "#f9f9f9" } }}>
                <TableCell>Average Invoice Size</TableCell>
                <TableCell align="right">{formatCurrency(amount.averageInvoiceSize)}</TableCell>
              </TableRow>
              <TableRow sx={{ '&:nth-of-type(even)': { backgroundColor: "#f9f9f9" } }}>
                <TableCell>Average Invoice Tenor (Days)</TableCell>
                <TableCell align="right">{Math.round(amount.averageInvoiceTenor)}</TableCell>
              </TableRow>
              <TableRow sx={{ '&:nth-of-type(even)': { backgroundColor: "#f9f9f9" } }}>
                <TableCell>Invoices Overdue Over 30 Days</TableCell>
                <TableCell align="right">{amount.invoicesOverdueOver30Days}</TableCell>
              </TableRow>
              <TableRow sx={{ '&:nth-of-type(even)': { backgroundColor: "#f9f9f9" } }}>
                <TableCell>Number Of Defaults</TableCell>
                <TableCell align="right">{amount.numberOfDefaults}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>
    </Card>
  );
};

export default InvestorStats;
